import type React from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '@iconify/react';
import { Modal, Button } from '@heroui/react';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';

type LedgerHandoffNoticeProps = {
    isOpen: boolean;
    onClose: () => void;
};

/**
 * Stands in for `JournalEntryModal` on the **retained-earnings rollover**, the
 * one action left whose handoff to the ledger is closed.
 *
 * ## The criterion
 *
 * *Both sides must be real.* Five domains have now met it and left this notice
 * behind: VAT, whose report is derived from real postings; fixed assets, with
 * all three of its flows, once the schedule and the disposal gain/loss became
 * the server's; payroll (BOS-109); and dividends (BOS-112), the last register
 * that computed from data existing nowhere but the client.
 *
 * ## Why the rollover is still here
 *
 * It fails the criterion from the other side. Its *commit* is real and verified
 * — `POST /ledger/fiscal-years/{year}/rollover` posts a genuine `ROLLOVER`
 * entry, guarded against duplicates. What is missing is the **preview**:
 * `/rollover/preview` returns 404 on both verbs, so there is no dry run for the
 * user to approve.
 *
 * That is not a smaller problem than a fabricated register, it is the same one
 * inverted — a consequential, permanent ledger write with nothing checkable in
 * front of it. The year-end close next to it *does* have a dry run, which is
 * why that action is live and this one is not. If such an entry were posted it
 * could not be removed: posted entries are immutable by design, and the only
 * remedy is a reversing entry, which leaves both the mistake and its correction
 * in the audit trail permanently.
 *
 * ## When this is deleted
 *
 * The day `/rollover/preview` lands. Swap it for `<JournalEntryModal>` at its
 * one call site and delete this component — `useRolloverMutation` and
 * `PeriodsService.postRetainedEarningsRollover` are already written and tested.
 * See `resources/journal/CONTRACT.md` §"Subsidiary handoffs".
 */
export const LedgerHandoffNotice: React.FC<LedgerHandoffNoticeProps> = ({
    isOpen,
    onClose,
}) => {
    const { t } = useTranslation('pages');

    return (
        <Modal isOpen={isOpen} onOpenChange={(open) => !open && onClose()}>
            <Modal.Backdrop>
                <Modal.Container size="sm">
                    <Modal.Dialog
                        className="w-full max-w-lg"
                        {...getNodeTestIdProp('ledger-handoff-notice')}
                    >
                        <Modal.CloseTrigger />
                        <Modal.Header>
                            <Modal.Heading>
                                <span className="flex items-center gap-2">
                                    <Icon
                                        aria-hidden
                                        icon="gravity-ui:circle-info"
                                    />
                                    {t('accounting.handoffDisabled.title')}
                                </span>
                            </Modal.Heading>
                        </Modal.Header>
                        <Modal.Body className="flex flex-col gap-3 pb-6">
                            <p
                                className="text-sm text-foreground"
                                {...getNodeTestIdProp(
                                    'ledger-handoff-notice-body',
                                )}
                            >
                                {t('accounting.handoffDisabled.rolloverBody')}
                            </p>
                            <p className="text-xs text-muted">
                                {t(
                                    'accounting.handoffDisabled.rolloverPreview',
                                )}
                            </p>
                            <div className="flex justify-end">
                                <Button
                                    onPress={onClose}
                                    variant="primary"
                                    {...getNodeTestIdProp(
                                        'ledger-handoff-notice-close',
                                    )}
                                >
                                    {t('accounting.handoffDisabled.close')}
                                </Button>
                            </div>
                        </Modal.Body>
                    </Modal.Dialog>
                </Modal.Container>
            </Modal.Backdrop>
        </Modal>
    );
};
